import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  alpha,
} from '@mui/material';
import {
  Person,
  Assignment,
  Close,
} from '@mui/icons-material';
import { Lead, User } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface AssignLeadModalProps {
  open: boolean;
  lead: Lead | null;
  onClose: () => void;
  onAssign: (leadId: string, userId: string) => void;
  isMobile?: boolean;
  users: User[];
}

const AssignLeadModal: React.FC<AssignLeadModalProps> = ({
  open,
  lead,
  onClose,
  onAssign,
  users,
}) => {
  const { user } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const handleAssign = () => {
    if (lead && selectedUserId) {
      onAssign(lead._id.$oid, selectedUserId);
      setSelectedUserId('');
      onClose();
      // A notificação de sucesso será mostrada no DashboardPage
    }
  };

  if (!lead) return null;

  // Para gerentes, mostrar apenas vendedores da sua equipe
  // Para admins, mostrar todos os vendedores
  const availableUsers = user?.role === 'admin'
    ? users.filter(u => u.role === 'vendedor')
    : user?.role === 'gerente'
      ? users.filter(u => u.role === 'vendedor' && u.managerId === user.id)
      : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #2196f3 0%, #00f5ff 100%)',
        color: 'white',
      }}>
        <Assignment sx={{ mr: 1 }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Atribuir Lead
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            color: 'white',
            minWidth: 'auto',
            p: 1,
          }}
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            p: 2,
            backgroundColor: alpha('#2196f3', 0.1),
            borderRadius: 2,
            border: `1px solid ${alpha('#2196f3', 0.2)}`,
            mb: 3,
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              {lead.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              📱 {lead.phoneNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              📧 {lead.email}
            </Typography>
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Selecionar Vendedor</InputLabel>
            <Select
              value={selectedUserId}
              label="Selecionar Vendedor"
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              {availableUsers.map((assignUser) => (
                <MenuItem key={assignUser.id} value={assignUser.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 2,
                        backgroundColor: '#2196f3',
                      }}
                    >
                      <Person fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {assignUser.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {assignUser.email}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {availableUsers.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              Nenhum vendedor disponível para atribuição
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ mr: 1 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedUserId}
          sx={{
            backgroundColor: '#2196f3',
            '&:hover': {
              backgroundColor: '#00f5ff',
            },
          }}
        >
          <Assignment sx={{ mr: 1 }} />
          Atribuir Lead
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignLeadModal;